"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XMarkIcon } from "@heroicons/react/24/solid";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showClose?: boolean;
    closeClassName?: string;
    closeIconClassName?: string;
  }
>(
  (
    {
      className,
      children,
      showClose = true,
      closeClassName,
      closeIconClassName,
      ...props
    },
    ref,
  ) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed inset-0 z-50 flex h-full flex-col duration-200 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-auto sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg",
        className,
      )}
      {...props}
    >
      <BodyScrollLock />
      {children}
      {showClose && (
        <DialogPrimitive.Close
          className={cn(
            "ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 z-20 cursor-pointer rounded-sm opacity-85 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none",
            closeClassName,
          )}
        >
          <XMarkIcon
            className={cn(
              "h-6 w-6 text-gray-500 hover:text-gray-700",
              closeIconClassName,
            )}
          />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const BodyScrollLock = () => {
  React.useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const dataset = body.dataset;
    const prevCount = Number(dataset.dialogScrollLockCount ?? "0");

    if (prevCount === 0) {
      dataset.dialogScrollLockOverflow = body.style.overflow ?? "";
      dataset.dialogScrollLockPaddingRight = body.style.paddingRight ?? "";
      dataset.dialogScrollLockHtmlOverflow = html.style.overflow ?? "";
      dataset.dialogScrollLockHtmlPaddingRight = html.style.paddingRight ?? "";

      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      body.style.overflow = "hidden";
      html.style.overflow = "hidden";
      if (scrollBarWidth > 0) {
        body.style.paddingRight = `${scrollBarWidth}px`;
        html.style.paddingRight = `${scrollBarWidth}px`;
      }
    }

    dataset.dialogScrollLockCount = String(prevCount + 1);

    return () => {
      const nextCount = Number(body.dataset.dialogScrollLockCount ?? "1") - 1;
      body.dataset.dialogScrollLockCount = String(Math.max(0, nextCount));

      if (nextCount <= 0) {
        body.style.overflow = body.dataset.dialogScrollLockOverflow ?? "";
        body.style.paddingRight =
          body.dataset.dialogScrollLockPaddingRight ?? "";
        html.style.overflow = body.dataset.dialogScrollLockHtmlOverflow ?? "";
        html.style.paddingRight =
          body.dataset.dialogScrollLockHtmlPaddingRight ?? "";
        delete body.dataset.dialogScrollLockOverflow;
        delete body.dataset.dialogScrollLockPaddingRight;
        delete body.dataset.dialogScrollLockHtmlOverflow;
        delete body.dataset.dialogScrollLockHtmlPaddingRight;
        delete body.dataset.dialogScrollLockCount;
      }
    };
  }, []);

  return null;
};

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg leading-none font-semibold tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
